<template>
  <v-layout
    id="UserDashboard"
    column
    wrap
  >
    <welcome-text-extensive v-if="targetsLoaded && isEmpty(targets)" />
    <v-flex
      v-if="targetsLoaded && averageValues.current"
      xs12
    >
      <!-- new user -->
      <v-card v-if="fillIndicatorSituation">
        <v-card-title primary-title>
          <div class="headline">
            {{ $t('message.fill_in_indicators_text') }}
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer /><v-btn
            class="secondary"
            @click="createNewIndicators"
          >
            {{ $t('message.to_indicators_button') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- "current situation" in progress - finish "current situation" reminder -->
      <v-card v-if="indicatorInProgress">
        <v-card-title primary-title>
          <div class="headline">
            {{ $t('message.finish_indicators_text') }}
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer /><v-btn
            class="secondary"
            :to="{
              name: 'indicators',
              params: { indicatorId: last(indicators)._id },
            }"
          >
            {{ $t('message.to_indicators_button') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- waiting for "current situation" approval -->
      <v-alert
        v-if="indicatorWaitingApproval"
        :value="true"
        type="warning"
      >
        {{ $t('message.indicators_not_approved_yet') }}
      </v-alert>

      <!-- "current situation" approved, but not targets yet -->
      <v-card v-if="fillTargets">
        <v-card-title primary-title>
          <div class="headline">
            {{ $t('message.fill_in_targets_text') }}
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer /><v-btn
            class="secondary"
            :to="{ name: 'targets', params: { targetId: 'own' } }"
          >
            {{ $t('message.fill_in_targets_button') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- targets in progress - finish targets reminder -->
      <v-card v-if="targetsInProgress">
        <v-card-title primary-title>
          <div class="headline">
            {{ $t('message.finish_targets_text') }}
          </div>
        </v-card-title>
        <v-card-actions>
          <v-spacer /><v-btn
            class="secondary"
            :to="{ name: 'targets', params: { targetId: 'own' } }"
          >
            {{ $t('message.fill_in_targets_button') }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Waiting for targets approval -->
      <v-alert
        :value="targetsWaitingApproval"
        type="warning"
      >
        {{ $t('message.targets_not_approved_yet') }}
      </v-alert>
      <v-layout
        row
        wrap
        fill-height
      >
        <v-flex
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForRollingAnualAvgProduction"
            :values="valuesForRollingAnualAvgProduction"
            :title="
              $t('message.dashboard_card_title_RollingAnualAvgProduction')
            "
            :fraction-digits="0"
            :platform-avg-current="
              averageValues.current.rollingAnualAvgProduction
            "
            :platform-avg-target="
              averageValues.target.rollingAnualAvgProduction
            "
          />
        </v-flex>
        <v-flex
          v-if="valuesForActualLifeBalance"
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForActualLifeBalance"
            :values="valuesForActualLifeBalance"
            unit="€"
            :title="$t('message.dashboard_card_title_ActualLifeBalance')"
            :fraction-digits="0"
            :platform-avg-current="averageValues.current.actualLifeBalance"
            :platform-avg-target="averageValues.target.actualLifeBalance"
          />
        </v-flex>
        <v-flex
          v-if="valuesForLifeBalance"
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForLifeBalance"
            :values="valuesForLifeBalance"
            unit="€"
            :title="$t('message.dashboard_card_title_LifeBalance')"
            :fraction-digits="0"
            :platform-avg-current="averageValues.current.lifeBalance"
            :platform-avg-target="averageValues.target.lifeBalance"
          />
        </v-flex>
        <v-flex
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForActualLivespanProduction"
            :values="valuesForActualLivespanProduction"
            :title="$t('message.dashboard_card_title_ActualLivespanProduction')"
            :platform-avg-current="
              averageValues.current.lifelongProductionPerCow
            "
            :platform-avg-target="averageValues.target.lifelongProductionPerCow"
          />
        </v-flex>
        <v-flex
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForHerdLivespan"
            :values="valuesForHerdLivespan"
            :title="$t('message.dashboard_card_title_HerdLivespan')"
            :fraction-digits="2"
            :platform-avg-current="averageValues.current.avgHerdLifePerCow"
            :platform-avg-target="averageValues.target.avgHerdLifePerCow"
          />
        </v-flex>
        <v-flex
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForProductiveLivespan"
            :values="valuesForProductiveLivespan"
            :title="$t('message.dashboard_card_title_ProductiveLivespan')"
            :fraction-digits="2"
            :platform-avg-current="averageValues.current.productiveLifespan"
            :platform-avg-target="averageValues.target.productiveLifespan"
          />
        </v-flex>
        <v-flex
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForLivespanProduction"
            :values="valuesForLivespanProduction"
            :title="$t('message.dashboard_card_title_LivespanProduction')"
            :platform-avg-current="averageValues.current.lifespanProduction"
            :platform-avg-target="averageValues.target.lifespanProduction"
          />
        </v-flex>
        <v-flex
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForLivespan"
            :values="valuesForLivespan"
            :title="$t('message.dashboard_card_title_Livespan')"
            :fraction-digits="2"
            :platform-avg-current="averageValues.current.lifespanCulling"
            :platform-avg-target="averageValues.target.lifespanCulling"
          />
        </v-flex>
        <v-flex
          xs12
          sm6
          md4
        >
          <dashboard-card
            v-if="valuesForProductiveLivespanCulling"
            :values="valuesForProductiveLivespanCulling"
            :title="
              $t('message.dashboard_card_title_ProductiveLivespanCulling')
            "
            :fraction-digits="2"
            :platform-avg-current="
              averageValues.current.productiveLifespanCulling
            "
            :platform-avg-target="
              averageValues.target.productiveLifespanCulling
            "
          />
        </v-flex>
      </v-layout>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import last from 'lodash/last'
import DashboardCard from './DashboardCard'
import WelcomeTextExtensive from './WelcomeTextExtensive'

export default {
  name: 'UserDashboard',
  components: { DashboardCard, WelcomeTextExtensive },
  props: {
    user: {
      type: Object,
      default: () => ({}),
    },
  },
  data: () => ({
    targetsLoaded: false,
    targets: {},
  }),
  computed: {
    valuesForRollingAnualAvgProduction() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current: this.lastApprovedIndicator.companyDetails
            .rollingAnualAvgProduction,
          target: this.targets.companyDetails.rollingAnualAvgProduction,
          date: this.lastApprovedIndicator.date,
          totalCurrent:
            this.lastApprovedIndicator.companyDetails
              .rollingAnualAvgProduction *
            this.lastApprovedIndicator.companyDetails.herdSize,
          totalTarget:
            this.targets.companyDetails.rollingAnualAvgProduction *
            this.targets.companyDetails.herdSize,
        }
      } else return null
    },
    lastApprovedIndicator() {
      return this.indicators.find((e) => e.isDraft === false && e.approvedOn)
    },
    valuesForLivespan() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current:
            this.lastApprovedIndicator.companyDetails.avgLifespanCulledCows /
            12,
          target: this.targets.companyDetails.avgLifespanCulledCows / 12,
          date: this.lastApprovedIndicator.date,
        }
      } else return null
    },
    valuesForHerdLivespan() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current:
            this.lastApprovedIndicator.companyDetails.avgHerdLifePerCow / 12,
          target: this.targets.companyDetails.avgHerdLifePerCow / 12,
          date: this.lastApprovedIndicator.date,
        }
      } else return null
    },
    valuesForProductiveLivespan() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current:
            this.valuesForHerdLivespan.current -
            this.lastApprovedIndicator.companyDetails
              .calvingAgeHeifersLastYearWrittenInMonths /
              12,
          target:
            this.valuesForHerdLivespan.target -
            this.targets.companyDetails
              .calvingAgeHeifersLastYearWrittenInMonths /
              12,
          date: this.lastApprovedIndicator.date,
        }
      } else return null
    },
    valuesForProductiveLivespanCulling() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current:
            this.lastApprovedIndicator.companyDetails.avgLifespanCulledCows /
              12 -
            this.lastApprovedIndicator.companyDetails
              .calvingAgeHeifersLastYearWrittenInMonths /
              12,
          target:
            this.targets.companyDetails.avgLifespanCulledCows / 12 -
            this.targets.companyDetails
              .calvingAgeHeifersLastYearWrittenInMonths /
              12,
          date: this.lastApprovedIndicator.date,
        }
      } else return null
    },
    valuesForLivespanProduction() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current: this.lastApprovedIndicator.companyDetails
            .avgLifelongProductionPerCulledCow,
          target: this.targets.companyDetails.avgLifelongProductionPerCulledCow,
          date: this.lastApprovedIndicator.date,
        }
      } else return null
    },
    valuesForActualLivespanProduction() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current: this.lastApprovedIndicator.companyDetails
            .lifelongProductionPerCow,
          target: this.targets.companyDetails.lifelongProductionPerCow,
          date: this.lastApprovedIndicator.date,
        }
      } else return null
    },
    valuesForLifeBalance() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current: this.lastApprovedIndicator.lifeBalance,
          target: this.targets.lifeBalance,
          date: this.lastApprovedIndicator.date,
          totalCurrent: this.lastApprovedIndicator.lifeBalanceTotal,
          totalTarget: this.targets.lifeBalanceTotal,
        }
      } else return null
    },
    valuesForActualLifeBalance() {
      if (!isEmpty(this.targets) && this.lastApprovedIndicator) {
        return {
          current: Math.round(
            this.calculateActualLifeBalance(this.lastApprovedIndicator)
          ),
          target: Math.round(this.calculateActualLifeBalance(this.targets)),
          date: this.lastApprovedIndicator.date,
          totalCurrent:
            Math.round(
              this.calculateActualLifeBalance(this.lastApprovedIndicator)
            ) * this.lastApprovedIndicator.companyDetails.herdSize,
          totalTarget:
            Math.round(this.calculateActualLifeBalance(this.targets)) *
            this.targets.companyDetails.herdSize,
        }
      } else return null
    },
    fillIndicatorSituation() {
      return isEmpty(this.indicators)
    },
    indicatorInProgress() {
      return !isEmpty(this.indicators) && last(this.indicators).isDraft
    },
    indicatorWaitingApproval() {
      return (
        !isEmpty(this.indicators) &&
        !last(this.indicators).isDraft &&
        isEmpty(last(this.indicators).approvedOn)
      )
    },
    fillTargets() {
      return (
        isEmpty(this.targets) &&
        !isEmpty(this.indicators) &&
        !isEmpty(last(this.indicators).approvedOn)
      )
    },
    targetsInProgress() {
      return !isEmpty(this.targets) && this.targets.isDraft
    },
    targetsWaitingApproval() {
      return (
        !isEmpty(this.targets) &&
        !this.targets.isDraft &&
        isEmpty(this.targets).approvedOn
      )
    },
    ...mapState({
      indicators: (state) => state.indicatorsList,
      averageValues: (state) => state.latestAverageValues,
      standard: (state) => state.standardValues,
      rolling: (state) => state.rollingValues,
    }),
  },
  created() {
    this.$store.dispatch('getStandardValues')
    this.$store.dispatch('getRollingValues')
    this.$store.dispatch('getLatestAverageValues')
    this.$store.subscribe((mutation, state) => {
      switch (mutation.type) {
      case 'onIndicatorsCreated':
        this.$router.push({
          name: 'indicators',
          params: { indicatorId: mutation.payload._id },
        })
        break
      case 'onTargets':
        this.targets = mutation.payload.data[0]
        this.targetsLoaded = true
        break
      }
    })
  },
  mounted() {
    if (isEmpty(this.targets)) {
      this.targetsLoaded = false
    }
    this.$store.dispatch('getTargets')
    this.$store.dispatch('getIndicatorsList')
  },
  methods: {
    isEmpty: isEmpty,
    calculateActualLifeBalance(values) {
      let x6 = Math.floor(values.companyDetails.avgHerdLifePerCow / 12)
      let x7 = values.companyDetails.avgHerdLifePerCow % 12

      let x9 = Math.floor(values.companyDetails.avgLifespanCulledCows / 12)
      let x10 = values.companyDetails.avgLifespanCulledCows % 12
      let x74 = values.companyDetails.calvingAgeHeifersLastYearWrittenInMonths

      let x218 = values.lifeBalance
      let x129 = x6 + x7 / 12 - x74 / 12
      let x134 = x9 + x10 / 12 - x74 / 12

      return (x218 / x134) * x129
    },
    createNewIndicators() {
      this.$store.dispatch('createIndicators', {
        date: new Date().toString(),
        isDraft: true,
      })
    },
    last(array) {
      //this scope
      return last(array)
    },
  },
}
</script>

<style scoped>
.container.grid-list-md .layout:not(:only-child) {
  margin: auto -10px;
}
.container.grid-list-md .layout .flex {
  padding: 10px !important;
}

.container.grid-list-md > .layout {
  max-width: 110rem;
  margin: 0 auto !important;
}
p {
  font-size: 1.1rem;
}
h2 {
  margin-bottom: 20px;
}
</style>
